/* Pump Companies — staff page to register external concrete-pump contractors
   and hand them a permanent QR-code tracking link so their operator can see
   which trucks are inbound for today's pours. */
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { PreviewLinkWarning } from "@/components/PreviewLinkWarning";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, QrCode, Link as LinkIcon, Trash2, Download, Printer, Copy, HardHat, ExternalLink, Handshake, CheckCircle2, Mail, MessageSquare, Construction } from "lucide-react";
import { toast } from "sonner";
import { printQrPage } from "@/lib/printQr";
import { PumpUnitsDialog } from "@/components/PumpUnitsDialog";

const emptyForm = { name: "", phone: "", email: "", contact_person: "", notes: "" };

export default function PumpCompanies() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [qrTarget, setQrTarget] = useState(null);  // pump company
  const [unitsFor, setUnitsFor] = useState(null);  // pump company whose pumps we're managing

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pump-companies");
      setList(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (pc) => { setEditing(pc); setForm({ ...emptyForm, ...pc }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Company name is required");
    try {
      if (editing) await api.put(`/pump-companies/${editing.id}`, form);
      else await api.post("/pump-companies", form);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (pc) => {
    if (!window.confirm(`Delete "${pc.name}"? Any jobs linked to this pump will be un-linked.`)) return;
    try {
      await api.delete(`/pump-companies/${pc.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const [invite, setInvite] = useState(null);   // { pc, url, email, phone }
  const inviteToNetwork = async (pc) => {
    try {
      const { data } = await api.post(`/pump-companies/${pc.id}/invite`);
      if (data.already_connected) { toast.success(`${pc.name} is already on the Partner Network`); return; }
      await navigator.clipboard?.writeText(data.url).catch(() => {});
      setInvite({ pc, ...data });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the invite"); }
  };
  const sendInviteEmail = async () => {
    try {
      await api.post(`/pump-companies/${invite.pc.id}/invite`, { send_email: true });
      toast.success(`Invite emailed to ${invite.email}`);
      setInvite(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the email"); }
  };

  const [share, setShare] = useState(null);   // { pc, url } — tracker link share sheet
  const copyLink = async (pc) => {
    try {
      const r = await api.post(`/pump-companies/${pc.id}/ensure-track-token`);
      const url = `${window.location.origin}/pump/${r.data.track_token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Tracker link copied"); } catch { /* shown in the dialog to copy manually */ }
      setShare({ pc, url });
    } catch (e) { toast.error("Failed to create link"); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="pump-companies-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-tag">CONTRACTORS</div>
          <h1 className="heading text-3xl font-black uppercase">Pump Companies</h1>
          <p className="text-sm text-muted-industrial mt-1">
            Register the concrete-pump crews you work with. Each gets a permanent QR-code tracker
            they can scan onsite to see incoming trucks + live ETAs.
          </p>
        </div>
        <Button data-testid="new-pump-btn" onClick={openNew} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">
          <Plus className="w-4 h-4 mr-1" />New Pump Company
        </Button>
      </div>

      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-industrial">
            <tr className="text-left">
              <th className="p-3 label-tag">Company</th>
              <th className="p-3 label-tag">Contact</th>
              <th className="p-3 label-tag">Phone</th>
              <th className="p-3 label-tag">Email</th>
              <th className="p-3 label-tag">Partner Network</th>
              <th className="p-3 label-tag text-right">Tracker</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-muted-industrial mono text-xs uppercase tracking-widest">
                {loading ? "Loading…" : "No pump companies yet · click 'New Pump Company' to add one"}
              </td></tr>
            )}
            {list.map((pc) => (
              <tr key={pc.id} data-testid={`pump-row-${pc.id}`} className="border-b border-industrial hover:bg-surface-2 cursor-pointer" onClick={() => openEdit(pc)}>
                <td className="p-3">
                  <div className="font-semibold">{pc.name}</div>
                  {pc.notes && <div className="text-xs text-muted-industrial mt-0.5 line-clamp-1">{pc.notes}</div>}
                </td>
                <td className="p-3">{pc.contact_person || <span className="text-muted-industrial">—</span>}</td>
                <td className="p-3 mono text-xs">{pc.phone || <span className="text-muted-industrial">—</span>}</td>
                <td className="p-3 mono text-xs">{pc.email || <span className="text-muted-industrial">—</span>}</td>
                <td className="p-3">
                  {pc.partner_id ? (
                    <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest text-emerald-500" data-testid={`pump-partner-live-${pc.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Connected
                    </span>
                  ) : (
                    <button
                      data-testid={`pump-invite-${pc.id}`}
                      onClick={(e) => { e.stopPropagation(); inviteToNetwork(pc); }}
                      title="Give them a free partner account so they can see their loads live"
                      className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-sky-500 text-sky-500 hover:bg-sky-500 hover:text-black"
                    >
                      <Handshake className="w-3 h-3" /> Invite
                    </button>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button
                      data-testid={`pump-units-${pc.id}`}
                      onClick={(e) => { e.stopPropagation(); setUnitsFor(pc); }}
                      title="Their individual pumps — add one and allocate it on an order"
                      className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-industrial hover:border-primary-orange hover:text-primary-orange"
                    >
                      <Construction className="w-3 h-3" />Pumps
                    </button>
                    <button
                      data-testid={`pump-qr-${pc.id}`}
                      onClick={(e) => { e.stopPropagation(); setQrTarget(pc); }}
                      title="Show / print QR code"
                      className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black"
                    >
                      <QrCode className="w-3 h-3" />QR
                    </button>
                    <button
                      data-testid={`pump-copy-${pc.id}`}
                      onClick={(e) => { e.stopPropagation(); copyLink(pc); }}
                      title="Copy the live-ETA tracker link to text or email this contractor"
                      className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-2 py-1 border border-emerald-500 text-emerald-500 hover:bg-emerald-500 hover:text-black"
                    >
                      <LinkIcon className="w-3 h-3" /> Send link
                    </button>
                    <button
                      data-testid={`pump-del-${pc.id}`}
                      onClick={(e) => { e.stopPropagation(); del(pc); }}
                      title="Delete"
                      className="text-muted-industrial hover:text-red-500 p-1.5"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit */}
      {unitsFor && <PumpUnitsDialog company={unitsFor} onClose={() => setUnitsFor(null)} />}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="pump-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase">{editing ? "Edit" : "New"} Pump Company</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="label-tag">Company Name *</Label>
              <Input data-testid="pump-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-transparent border-industrial mono" placeholder="e.g. South Coast Pumping" />
            </div>
            <div>
              <Label className="label-tag">Contact Person</Label>
              <Input data-testid="pump-contact" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
            </div>
            <div>
              <Label className="label-tag">Phone</Label>
              <Input data-testid="pump-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
            </div>
            <div className="col-span-2">
              <Label className="label-tag">Email</Label>
              <Input data-testid="pump-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none bg-transparent border-industrial mono" />
            </div>
            <div className="col-span-2">
              <Label className="label-tag">Notes</Label>
              <textarea data-testid="pump-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full rounded-none bg-transparent border border-industrial mono text-sm p-2" placeholder="Pump type, boom reach, standby rate, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="pump-save" onClick={save} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <PumpQRDialog open={!!qrTarget} onOpenChange={(v) => !v && setQrTarget(null)} pump={qrTarget} />

      {/* Share tracker link */}
      <Dialog open={!!share} onOpenChange={(v) => !v && setShare(null)}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="pump-share-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase inline-flex items-center gap-2"><LinkIcon className="w-4 h-4 text-emerald-500" /> Send tracker link · {share?.pc?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-industrial">
            Send this link to {share?.pc?.contact_person || "the contractor"}. They open it on their phone — no login — and watch
            which trucks are inbound, where each one is and the live ETA for every load on their pump.
          </p>
          <PreviewLinkWarning what="tracker link" />
          <div className="mono text-[11px] break-all border border-industrial px-2 py-2 select-all" data-testid="pump-share-url">{share?.url}</div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="pump-share-copy" onClick={() => { navigator.clipboard?.writeText(share.url); toast.success("Copied"); }}
              className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</Button>
            {share?.pc?.phone && (
              <a href={`sms:${share.pc.phone}?&body=${encodeURIComponent(`Track your loads live: ${share.url}`)}`}
                data-testid="pump-share-sms" className="inline-flex items-center gap-1 mono text-[11px] uppercase tracking-widest px-4 border border-industrial hover:border-primary-orange hover:text-primary-orange">
                <MessageSquare className="w-3.5 h-3.5" /> Text {share.pc.phone}
              </a>
            )}
            {share?.pc?.email && (
              <a href={`mailto:${share.pc.email}?subject=${encodeURIComponent("Your live truck tracker")}&body=${encodeURIComponent(`Hi ${share.pc.contact_person || ""},\n\nTrack your inbound loads and live ETAs here (no login needed):\n${share.url}\n`)}`}
                data-testid="pump-share-email" className="inline-flex items-center gap-1 mono text-[11px] uppercase tracking-widest px-4 border border-industrial hover:border-primary-orange hover:text-primary-orange">
                <Mail className="w-3.5 h-3.5" /> Email {share.pc.email}
              </a>
            )}
            <a href={share?.url} target="_blank" rel="noreferrer" data-testid="pump-share-open"
              className="inline-flex items-center gap-1 mono text-[11px] uppercase tracking-widest px-4 border border-industrial hover:border-primary-orange hover:text-primary-orange">
              <ExternalLink className="w-3.5 h-3.5" /> Preview
            </a>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partner Network invite */}
      <Dialog open={!!invite} onOpenChange={(v) => !v && setInvite(null)}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="pump-invite-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase inline-flex items-center gap-2"><Handshake className="w-4 h-4 text-sky-500" /> Invite {invite?.pc?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-industrial">
            Send them this link. They tap <b className="text-ink">Free partner account</b> on it and they're in —
            already connected to you, seeing which truck is coming, where it is and the ETA for every load on
            their pump. Fewer "where's my truck" calls to your dispatcher.
          </p>
          <PreviewLinkWarning what="invite link" />
          <div className="mono text-[11px] break-all border border-industrial px-2 py-2" data-testid="pump-invite-url">{invite?.url}</div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="pump-invite-copy" onClick={() => { navigator.clipboard?.writeText(invite.url); toast.success("Copied"); }}
              className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</Button>
            {invite?.phone && (
              <a href={`sms:${invite.phone}?&body=${encodeURIComponent(`${invite.supplier} here — follow your loads live on ArmorBatch (free): ${invite.url}`)}`}
                data-testid="pump-invite-sms" className="inline-flex items-center gap-1 mono text-[11px] uppercase tracking-widest px-4 border border-industrial hover:border-primary-orange hover:text-primary-orange">
                <MessageSquare className="w-3.5 h-3.5" /> Text {invite.phone}
              </a>
            )}
            {invite?.email && (
              <Button variant="outline" data-testid="pump-invite-email" onClick={sendInviteEmail}
                className="rounded-none uppercase tracking-widest text-xs font-bold border-industrial"><Mail className="w-3.5 h-3.5 mr-1" /> Email {invite.email}</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PumpQRDialog({ open, onOpenChange, pump }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !pump) { setUrl(""); return; }
    (async () => {
      setLoading(true);
      try {
        const r = await api.post(`/pump-companies/${pump.id}/ensure-track-token`);
        setUrl(`${window.location.origin}/pump/${r.data.track_token}`);
      } catch { toast.error("Could not create tracker link"); }
      finally { setLoading(false); }
    })();
  }, [open, pump]);

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const downloadPng = () => {
    const svg = document.getElementById("pump-qr-svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 800; canvas.height = 800;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 800, 800);
      ctx.drawImage(img, 40, 40, 720, 720);
      canvas.toBlob((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `gadtech-pump-tracker-${(pump?.name || "pump").replace(/\s+/g, "_")}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
        URL.revokeObjectURL(svgUrl);
      }, "image/png");
    };
    img.src = svgUrl;
  };

  const printPage = () => {
    printQrPage({
      title: `Pump Tracker · ${pump?.name || ""}`,
      tagLine: "ARMORBATCH GLOBAL · Pump Tracker",
      heading: pump?.name || "",
      description: "Scan this QR with your phone camera onsite to see every concrete truck coming to you today — live GPS, ETA, and volume delivered. Bookmark the page; the link is permanent.",
      footer: "© ARMORBATCH GLOBAL · Live pump tracker",
      url,
      svgElementId: "pump-qr-svg",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-industrial max-w-md" data-testid="pump-qr-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase inline-flex items-center gap-2">
            <HardHat className="w-4 h-4 text-primary-orange" />
            Pump Tracker · <span className="text-primary-orange">{pump?.name}</span>
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="label-tag text-primary-orange pulse-orange py-4">Generating…</div>}

        {url && !loading && (
          <>
            <div className="flex justify-center py-4">
              <div className="p-4 bg-white rounded-xl border-2 border-primary-orange">
                <QRCodeSVG id="pump-qr-svg" value={url} size={220} level="M" includeMargin={false} />
              </div>
            </div>
            <div className="mono text-[10px] text-muted-industrial break-all border border-industrial rounded-md px-2 py-1.5 text-center">
              {url}
            </div>
            <div className="flex gap-2 mt-3">
              <Button data-testid="pump-qr-copy" onClick={copyUrl} variant="outline" className="rounded-lg border-industrial flex-1"><Copy className="w-4 h-4 mr-2" /> Copy URL</Button>
              <Button data-testid="pump-qr-download" onClick={downloadPng} variant="outline" className="rounded-lg border-industrial flex-1"><Download className="w-4 h-4 mr-2" /> PNG</Button>
              <Button data-testid="pump-qr-print" onClick={printPage} className="btn-cta flex-1"><Printer className="w-4 h-4 mr-2" /> Print</Button>
            </div>
            <a href={url} target="_blank" rel="noreferrer" className="mt-3 mono text-[10px] uppercase tracking-widest text-primary-orange hover:underline inline-flex items-center gap-1 justify-center w-full" data-testid="pump-qr-preview">
              <ExternalLink className="w-3 h-3" /> Preview tracker
            </a>
            <p className="text-[11px] text-muted-industrial mt-3 text-center">
              Print &amp; hand to your pump operator · email the PNG · SMS the link · bookmark on their phone.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
